import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { RefreshCw } from "lucide-react";
import { reportService } from "../api/reportService";

const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "Rp 0";
  return `Rp ${num.toLocaleString("id-ID")}`;
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Agu",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const formatMonthLabel = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "-";

  // Handle numeric month values like 1..12
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
    return MONTH_NAMES[numeric - 1];
  }

  // Handle formats like YYYY-MM or YYYY-MM-DD
  const yyyyMm = value.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
  if (yyyyMm) {
    const month = Number(yyyyMm[2]);
    if (month >= 1 && month <= 12) return MONTH_NAMES[month - 1];
  }

  return value;
};

export default function FinancialChart({ appliedRange, onApplyRange }) {
  const currentYear = new Date().getFullYear();
  const [startMonth, setStartMonth] = useState(appliedRange?.startMonth || `${currentYear}-01`);
  const [endMonth, setEndMonth] = useState(appliedRange?.endMonth || `${currentYear}-12`);
  const [chartRows, setChartRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (appliedRange) {
      setStartMonth(appliedRange.startMonth);
      setEndMonth(appliedRange.endMonth);
    }
  }, [appliedRange]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await reportService.getDashboard({
        chart_granularity: "MONTHLY",
        chart_start_month: appliedRange?.startMonth,
        chart_end_month: appliedRange?.endMonth,
      });

      setChartRows(response?.data?.financial_chart || []);
    } catch (err) {
      console.error("Failed to load financial chart:", err);
      setError(err?.message || "Gagal memuat grafik keuangan");
      setChartRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appliedRange) {
      loadChartData();
    }
  }, [appliedRange]);

  const categories = useMemo(() => {
    return chartRows.map((row) =>
      formatMonthLabel(row.month || row.period || row.label || row.year || "-"),
    );
  }, [chartRows]);

  const series = useMemo(() => {
    return [
      {
        name: "Pemasukan",
        data: chartRows.map((row) => Number(row.income || 0)),
      },
      {
        name: "Pengadaan",
        data: chartRows.map((row) => Number(row.procurement || 0)),
      },
      {
        name: "Pengeluaran",
        data: chartRows.map((row) => Number(row.expense || 0)),
      },
    ];
  }, [chartRows]);

  const handleApplyRange = () => {
    if (!startMonth || !endMonth) {
      setError("Bulan awal dan akhir wajib diisi.");
      return;
    }
    if (startMonth > endMonth) {
      setError("Bulan awal tidak boleh lebih besar dari bulan akhir.");
      return;
    }
    onApplyRange({ startMonth, endMonth });
  };

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    colors: ["#10B981", "#EF4444", "#F59E0B"], // Green, Red, Yellow
    stroke: {
      curve: "smooth",
      width: 2,
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: "#9CA3AF" },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#9CA3AF" },
      },
    },
    grid: {
      borderColor: "#F3F4F6",
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val) => formatCurrency(val),
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      markers: {
        radius: 12,
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200/70 mb-8 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 ring-1 ring-teal-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="M7 14l4-4 4 4 5-5" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              Grafik Keuangan
            </h2>
            <p className="text-xs text-slate-500 leading-tight">
              Ringkasan pemasukan, pengadaan & pengeluaran
            </p>
          </div>
        </div>
        <div className="w-full md:w-[412px] flex-shrink-0 flex items-center justify-between gap-2">
          <input
            type="month"
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            className="w-[130px] shrink-0 px-2.5 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
          />
          <input
            type="month"
            value={endMonth}
            onChange={(e) => setEndMonth(e.target.value)}
            className="w-[130px] shrink-0 px-2.5 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
          />
          <button
            onClick={handleApplyRange}
            className="w-[85px] shrink-0 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium shadow-sm shadow-teal-600/20 text-center">
            Terapkan
          </button>
          <button
            onClick={() => onApplyRange({ startMonth, endMonth })}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-60 transition-colors flex-shrink-0"
            title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-4 text-sm text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      <div className="h-80">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500">
            Memuat data grafik...
          </div>
        ) : (
          <Chart options={options} series={series} type="line" height="100%" />
        )}
      </div>
    </div>
  );
}
